import moment from 'moment-timezone';
import { EventModel } from '../models/EventModel';

export const MIN_EVENTS_FOR_VENUE_AGGREGATION = 5;

export type AggregatedVenueGroup = {
    venueName: string;
    venueSlug?: string;
    events: EventModel[];
    anchorDate?: string;
};

export type EventListItem =
    | {
        type: 'event';
        event: EventModel;
        anchorDate?: string;
    }
    | {
        type: 'aggregatedVenue';
        group: AggregatedVenueGroup;
        anchorDate?: string;
    };

function normalizeVenueName(value: string | undefined): string {
    return (value ?? '').trim();
}

function normalizeEventDay(value: string | undefined): string {
    return value ? moment.tz(value, 'America/Toronto').format('YYYY-MM-DD') : '';
}

function getAggregationKey(event: EventModel): string | null {
    const venueName = normalizeVenueName(event.venue?.name || event.locationName);
    const venueSlug = normalizeVenueName(event.venue?.slug || venueName);
    const eventDay = normalizeEventDay(event.dates[0]?.date);

    if (!venueName || !venueSlug || !eventDay) {
        return null;
    }

    return `${venueSlug}::${eventDay}`;
}

export function buildEventListItems(events: EventModel[]): EventListItem[] {
    const eventsByVenueAndDay = new Map<string, EventModel[]>();

    events.forEach((event) => {
        const aggregationKey = getAggregationKey(event);
        if (!aggregationKey) {
            return;
        }

        const groupEvents = eventsByVenueAndDay.get(aggregationKey) ?? [];
        groupEvents.push(event);
        eventsByVenueAndDay.set(aggregationKey, groupEvents);
    });

    const aggregatedKeys = new Set(
        Array.from(eventsByVenueAndDay.entries())
            .filter(([, groupEvents]) => groupEvents.length >= MIN_EVENTS_FOR_VENUE_AGGREGATION)
            .map(([aggregationKey]) => aggregationKey)
    );

    const seenAggregatedKeys = new Set<string>();
    const items: EventListItem[] = [];

    events.forEach((event) => {
        const venueName = normalizeVenueName(event.venue?.name || event.locationName);
        const venueSlug = normalizeVenueName(event.venue?.slug || venueName);
        const anchorDate = event.dates[0]?.date;
        const aggregationKey = getAggregationKey(event);

        if (!venueName || !venueSlug || !aggregationKey || !aggregatedKeys.has(aggregationKey)) {
            items.push({
                type: 'event',
                event,
                anchorDate,
            });
            return;
        }

        if (seenAggregatedKeys.has(aggregationKey)) {
            return;
        }

        seenAggregatedKeys.add(aggregationKey);
        const venueEvents = eventsByVenueAndDay.get(aggregationKey) ?? [];

        items.push({
            type: 'aggregatedVenue',
            group: {
                venueName,
                venueSlug,
                events: venueEvents,
                anchorDate: venueEvents[0]?.dates[0]?.date,
            },
            anchorDate: venueEvents[0]?.dates[0]?.date,
        });
    });

    return items;
}
